import classNames from "classnames";
import ListItem, { ListItemProps } from "../ListItem/ListItem";
import IconButton from "../IconButton";
import { MaterialNames } from "../material_symbol_names";

interface DetailSection {
  heading: string;
  options: ListItemProps[];
  renderEdit?: () => React.ReactNode;
  // TODO -- drop support for these once Facility Details page is refactored into partial edit forms
  headingAction?: () => void;
  headingActionIconName?: MaterialNames;
}

export interface MultiSectionDetailsCardProps {
  className?: string;
  sections: DetailSection[];
  editingSection?: string | null;
  setEditingSection?: (section: string | null) => void;
}

export default function MultiSectionDetailsCard({
  className = "",
  sections = [],
  editingSection,
  setEditingSection
}: MultiSectionDetailsCardProps) {
  // Include sections with empty options if they're editable (have renderEdit)
  const filteredSections = sections.filter((section) => section.options.length > 0 || section.renderEdit != null);
  if (filteredSections.length === 0) return null;

  return (
    <div
      data-testid="multi-section-details-card"
      className={classNames(
        "flex flex-col items-start h-full",
        className,
        "border border-sys-brand-outline rounded-lg"
      )}
    >
      {filteredSections.map(({ heading, options, renderEdit, headingAction, headingActionIconName }, index) => {
        const editable = renderEdit != null;
        const disabled = editingSection != null && editingSection !== heading;
        const isEditingSection = editingSection === heading;
        const isEmpty = options.length === 0;
        return (
          <div
            key={index}
            className={classNames("flex flex-col w-full px-6", {
              "pt-6 pb-4": !editable && index === 0,
              "pt-4 pb-4": editable || index > 0,
              "opacity-50": disabled
            })}
            data-testid={heading}
          >
            <div className="flex flex-row items-center justify-between">
              <p className={classNames(editable ? "my-2" : "mb-2", "text-title-large-bold font-serif")}>{heading}</p>
              {headingAction != null && headingActionIconName != null && (
                <IconButton onClick={headingAction} name={headingActionIconName} />
              )}
              {editable && !isEditingSection && (
                <IconButton
                  name="edit"
                  skipMargin
                  containerClassName="-mr-[10px]"
                  onClick={() => setEditingSection?.(heading)}
                  disabled={disabled}
                  data-testid={`${heading}-edit-button`}
                />
              )}
            </div>

            {editable && isEditingSection ? (
              <div className="mt-4 mb-2">{renderEdit?.()}</div>
            ) : isEmpty ? (
              <div className="text-body-medium">
                <span className="opacity-20">--</span>
              </div>
            ) : (
              options.map((item, index) => (
                <div key={index} className="flex items-start">
                  <ListItem {...item} iconBackground={true} />
                  {item.renderActions && item.renderActions()}
                </div>
              ))
            )}
          </div>
        );
      })}
    </div>
  );
}
